// ============================================================================
// L'ONGLET « STATS » de la fiche article — relevé maquette l.4037-4104.
//
// ⚠️ TOUT EST MESURÉ. La maquette fabrique ses chiffres (« remise moyenne 6 % », un nombre de semaines
// tiré de l'index de l'article) : légitime pour un prototype sans base, interdit ici (§23.0). Chaque
// valeur vient du serveur, ou s'affiche « N/C » avec sa raison.
//
// ⚠️ CE QUI EST INDISPONIBLE SE DIT AU LIEU DE VALOIR ZÉRO. Un taux d'écoulement sans aucune entrée
// n'est pas 0 % — il n'existe pas. Zéro se lirait « rien ne se vend », alors que la vérité est
// « on ne sait pas ce qui est entré ».
// ============================================================================
import { useEffect, useState } from 'react';
import { formatMoney, formatMinor } from '@orgazone/money-format';
import { divRoundHalfUp, pctBp, rescale } from '@orgazone/core-money';
import { ChartTip, Trans } from '@orgazone/ui-web';
import { api, type ArticleStats } from '../api';
import { nombre, t } from '../i18n';

/** Ce que les tuiles de performance affichent — `null` partout où le nombre n'existe pas. */
export interface IndicateursArticle {
  /** Part des entrées déjà vendues, en points de base. */
  ecoulementBp: number | null;
  /** Ventes par semaine sur la durée mesurée — affichée telle quelle. */
  vitesse: number | null;
  /** Semaines de stock au rythme observé. */
  couverture: number | null;
  /** Coût d'achat des ventes, ramené aux centimes. */
  coutCents: number;
  /** Chiffre d'affaires HT — venu du serveur, ventilé par taux. */
  caHt: number | null;
  /** Le coût est-il exploitable ? LA garde des deux tuiles de marge. */
  coutConnu: boolean;
  margeBrute: number | null;
  margeBp: number | null;
  prixMoyen: number | null;
}

/**
 * LES INDICATEURS DE PERFORMANCE D'UN ARTICLE — une seule dérivation, éprouvable.
 *
 * ⚠️ CHAQUE RATIO EST GARDÉ PAR SON DÉNOMINATEUR. Un taux sur zéro entrée, une vitesse sans durée
 * mesurée, une couverture sans vitesse : trois cas où le nombre n'existe pas et où zéro mentirait.
 *
 * ⚠️ TOUT PASSE PAR LES CŒURS (audit 2026-08-25). Ces lignes portaient une arithmétique en FLOTTANT —
 * et surtout une TROISIÈME définition de la marge, à côté de celle du SQL de la liste et de celle de
 * l'onglet Prix. Trois arrondis, trois bases, aucun libellé pour dire laquelle répond à quelle
 * question. `pctBp` et `divRoundHalfUp` (core-money) portent les ratios, `rescale` le changement
 * d'échelle — half-up SYMÉTRIQUE, là où `Math.round` arrondit vers +∞ sur les négatifs (un coût net
 * négatif existe : les retours valorisés peuvent dépasser les ventes).
 *
 * ⚠️ LE CA HT VIENT DU SERVEUR, ventilé PAR TAUX par `core-vat` (§30.3). Il était divisé par 1,2 : un
 * taux codé en dur, donc une marge fausse sur tout ce qui n'est pas à 20 % — et crédible.
 *
 * ⚠️ UNE SEULE GARDE POUR LES DEUX MARGES. « Marge brute » exigeait un coût NON NUL et « Marge
 * réalisée » un coût POSITIF : sur un coût net négatif, la première affichait un montant pendant que
 * la seconde, juste au-dessus, annonçait « aucun coût d'achat sur les ventes ». Deux tuiles côte à
 * côte qui se contredisaient.
 *
 * ⚠️ EXTRAITE DU COMPOSANT LE 2026-09-09 : ces trois corrections d'audit étaient documentées et
 * qu'aucun test ne tenait — un correctif sans garde se défait à la première réécriture.
 */
export function indicateursArticle(s: ArticleStats): IndicateursArticle {
  const ecoulementBp = s.entryQty > 0 ? pctBp(s.soldQty, s.entryQty) : null;
  const vitesse = s.weeks !== null && s.weeks > 0 ? s.soldQty / s.weeks : null;
  const couverture = vitesse !== null && vitesse > 0 ? Math.round(s.stockQty / vitesse) : null;
  const coutCents = rescale(s.costFine, 4, 2);
  const caHt = s.revenueExclTaxCents > 0 ? s.revenueExclTaxCents : null;
  const coutConnu = caHt !== null && coutCents > 0;
  return {
    ecoulementBp, vitesse, couverture, coutCents, caHt, coutConnu,
    margeBrute: coutConnu ? caHt - coutCents : null,
    margeBp: coutConnu ? pctBp(caHt - coutCents, caHt) : null,
    prixMoyen: s.soldQty > 0 ? divRoundHalfUp(s.revenueCents, s.soldQty) : null,
  };
}

/** Les deux lignes d'une tuile de KPI : la valeur, puis la ligne qui dit d'où elle sort. */
type KpiLines = readonly [(x: string) => React.ReactNode, (x: string) => React.ReactNode];

/**
 * Une tuile de KPI — libellé, valeur, et la ligne qui DIT d'où elle sort.
 * ⚠️ UNE VALEUR QUI EST UN COMPTE NE SE PASSE PAS EN `valeur` + `detail` : sa légende nomme ce qui est compté, et
 *    reçue à part elle ne s'accorde jamais (« 3 / pièce perdue à l'inventaire »). Elle passe par `count`, qui prête
 *    les deux lignes à une phrase comptée.
 */
function Kpi(props: { label: string; tone?: 'ok' | 'alerte' | 'attention' }
  & ({ valeur: string; detail: string } | { count: (lines: KpiLines) => React.ReactNode })) {
  const { label, tone } = props;
  const encre = tone === 'ok' ? 'var(--teal-700)'
    : tone === 'alerte' ? 'oklch(0.45 0.15 25)'
      : tone === 'attention' ? 'oklch(0.45 0.12 70)'
        : 'var(--ink-900)';
  const lines: KpiLines = [
    (x) => (
      <div style={{
        fontSize: 'var(--t-feature)', fontWeight: 800, letterSpacing: '-0.02em', color: encre, marginTop: 3,
        fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
      }}>{x}</div>
    ),
    (x) => <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-400)', marginTop: 2 }}>{x}</div>,
  ];
  return (
    <div style={{
      border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)',
      padding: '13px 14px', background: 'var(--paper)', minWidth: 0,
    }}>
      <div style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--ink-500)' }}>{label}</div>
      {'count' in props ? props.count(lines) : <>{lines[0](props.valeur)}{lines[1](props.detail)}</>}
    </div>
  );
}

/** Rangée de quatre tuiles — la grille de la maquette (l.4047). */
function Rangee({ children, haut }: { children: React.ReactNode; haut?: boolean }) {
  return <div className="oz-stat-kpis" style={{ marginTop: haut ? 0 : 11 }}>{children}</div>;
}

const day = (ms: number | null): string => {
  if (!ms) return '—';
  const d = new Date(ms);
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: '2-digit' });
};

export function StatsArticle({ modelKey }: { modelKey: string }) {
  const [s, setS] = useState<ArticleStats | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    let vivant = true;
    api.articleStats(modelKey)
      .then((r) => { if (vivant) { setS(r); setFailure(null); } })
      .catch((e: unknown) => { if (vivant) setFailure(e instanceof Error ? e.message : String(e)); });
    return () => { vivant = false; };
  }, [modelKey]);

  if (failure !== null) {
    return <p role="alert" style={{ margin: 0, fontSize: 'var(--t-note)', color: 'var(--danger-ink)' }}>
      
      {t('Les statistiques n’ont pas pu être lues : {failure}', { failure })}
    </p>;
  }
  if (s === null) return <p style={{ margin: 0, fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>{t('Mesure en cours…')}</p>;

  const { ecoulementBp, vitesse, couverture, margeBrute, margeBp, prixMoyen } = indicateursArticle(s);

  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 'var(--t-h4)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Performance de l’article')}</div>
        <p style={{ margin: '3px 0 0', fontSize: '12.5px', color: 'var(--ink-500)' }}>
          
          {s.firstEntryAt
            ? t('Magasin courant, depuis la première réception ({date}).', { date: day(s.firstEntryAt) })
            : t('Magasin courant — aucune réception enregistrée.')}
        </p>
      </div>

      <Rangee haut>
        <Kpi
          label={t('Taux d’écoulement')}
          valeur={ecoulementBp === null ? 'N/C' : `${formatMinor(ecoulementBp, { scale: 2, displayScale: 0 })} %`}
          detail={ecoulementBp === null ? t('aucune entrée en stock') : t('{vendues} sur {entrees}', { vendues: t('{n} vendue', { n: s.soldQty }), entrees: t('{n} entrée', { n: s.entryQty }) })}
          tone={ecoulementBp === null ? undefined : ecoulementBp >= 6000 ? 'ok' : ecoulementBp >= 3000 ? undefined : 'alerte'}
        />
        <Kpi
          label={t('Vitesse de vente')}
          valeur={vitesse === null ? t('N/C') : t('{v} par semaine', { v: nombre(Math.round(vitesse * 10) / 10) })}
          detail={s.weeks === null ? t('aucune réception pour borner la durée') : t('moyenne sur {n} semaine', { n: s.weeks })}
        />
        <Kpi
          label={t('Couverture de stock')}
          valeur={couverture === null ? (s.stockQty > 0 ? '∞' : '—') : t('{v} sem', { v: couverture })}
          detail={vitesse === null || vitesse === 0
            ? t('rien ne se vend : la couverture n’a pas de fin')
            : t('{n} pièce au rythme actuel', { n: s.stockQty })}
          tone={couverture !== null && couverture > 26 ? 'attention' : undefined}
        />
        <Kpi
          label={t('Marge réalisée')}
          valeur={margeBp === null ? 'N/C' : `${formatMinor(margeBp, { scale: 2, displayScale: 1 })} %`}
          detail={margeBp === null ? t('aucun coût d’achat sur les ventes') : t('remises de caisse incluses')}
        />
      </Rangee>

      <Rangee>
        <Kpi
          label={t('CA TTC cumulé')}
          valeur={formatMoney(s.revenueCents, { thousandsSep: ' ' })}
          detail={t('{n} pièce vendue', { n: s.soldQty })}
        />
        <Kpi
          label={t('Marge brute')}
          valeur={margeBrute === null ? 'N/C' : formatMoney(margeBrute, { thousandsSep: ' ' })}
          detail={t('CA HT − coût des ventes')}
        />
        <Kpi
          label={t('Prix moyen vendu')}
          valeur={prixMoyen === null ? 'N/C' : formatMoney(prixMoyen, { thousandsSep: ' ' })}
          detail={prixMoyen === null ? t('aucune vente') : t('TTC, remises déduites')}
        />
        {/* ⚠️ « Démarque » compte les AJUSTEMENTS NÉGATIFS. Zéro y est une vraie valeur — rien n'a été
            perdu — d'où le ton vert plutôt qu'un « N/C ». */}
        <Kpi
          label={t('Démarque')}
          count={(lines) => <Trans k='<1>{n}</1> <2>pièce perdue à l’inventaire</2>' vars={{ n: s.shrinkQty }} parts={lines} />}
          tone={s.shrinkQty > 0 ? 'alerte' : 'ok'}
        />
      </Rangee>

      <Histogramme semaines={s.weekly} />

      {/* ⚠️ DEUX BLOCS, DEUX TEINTES ET DEUX GABARITS (relevé l.4075-4092) — ce n’est pas une
          coquetterie : le teal des tailles et le bleu des coloris permettent de savoir quel bloc on
          lit quand ils passent l’un sous l’autre en écran étroit. Et un libellé de coloris
          (« Bleu marine ») ne tient pas dans les 46 px d’une taille. */}
      <div className="oz-stat-split" style={{ marginTop: 11 }}>
        <Repartition heading={t('Ventes par taille')} parts={s.bySize} labelWidth={46} mono trait="var(--teal-500)" />
        <Repartition heading={t('Ventes par coloris')} parts={s.byColor} labelWidth={86} trait="var(--blue)" />
      </div>

      <div style={{
        display: 'flex', gap: 22, flexWrap: 'wrap', marginTop: 11, padding: '13px 16px',
        borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)',
      }}>
        {([
          [t('Première réception'), s.firstEntryAt], [t('Dernière réception'), s.lastEntryAt],
          [t('Première vente'), s.firstSaleAt], [t('Dernière vente'), s.lastSaleAt],
        ] as const).map(([l, v]) => (
          <div key={l}>
            <div style={{
              fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.05em',
              textTransform: 'uppercase', color: 'var(--ink-400)',
            }}>{l}</div>
            <div style={{ fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)', marginTop: 2 }}>{day(v)}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 11, fontSize: '11.5px', color: 'var(--ink-400)', lineHeight: 1.5 }}>
        
        {t('Taux d’écoulement = ventes ÷ entrées. Couverture = stock actuel ÷ vitesse moyenne. Chiffres du magasin courant, remises de caisse incluses, retours et annulations déduits.')}
      </div>
    </div>
  );
}

/**
 * Les douze dernières semaines.
 * ⚠️ LES SEMAINES SANS VENTE SONT RENDUES, à hauteur nulle : les retirer resserrerait le graphique et
 * ferait lire une régularité qui n'existe pas — or ce sont les creux qu'on vient y voir.
 * ⚠️ L'INFOBULLE EST CELLE DU SOCLE (`ChartTip` — demande utilisateur 2026-08-24 : « le même design de
 * tooltip PARTOUT ») : la première version posait un `title` natif du navigateur, un design étranger à
 * côté des bulles du tableau de bord. Le geste survol/quitter est le même que `Bars`.
 */
function Histogramme({ semaines }: { semaines: ArticleStats['weekly'] }) {
  const [survol, setSurvol] = useState<{ i: number; x: number; y: number } | null>(null);
  const total = semaines.reduce((n, w) => n + w.qty, 0);
  const max = Math.max(1, ...semaines.map((w) => w.qty));
  const etiquette = (i: number) => (i === semaines.length - 1 ? 'S' : `S-${semaines.length - 1 - i}`);
  return (
    <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', padding: '15px 16px', marginTop: 11 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Ventes des 12 dernières semaines')}</div>
        <div style={{ fontSize: '11.5px', color: 'var(--ink-500)' }}>
          <Trans k='<1>{total}</1> pièces' vars={{ total }} parts={[(x) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{x}</span>]} /></div>
      </div>
      <div role="list" style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 86, marginTop: 12 }}
        onMouseLeave={() => setSurvol(null)}>
        {semaines.map((w, i) => (
          <div
            key={w.start}
            role="listitem"
            aria-label={t('{semaine} : {n} vente', { semaine: etiquette(i), n: w.qty })}
            onMouseMove={(e) => setSurvol({ i, x: e.clientX, y: e.clientY })}
            style={{
              flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%', gap: 4,
            }}
          >
            <div
              style={{
                height: `${Math.round((w.qty / max) * 100)}%`, minHeight: 2,
                // La semaine en cours est appuyée : c'est celle qu'on lit en premier.
                background: i === semaines.length - 1 ? 'var(--teal-500)' : 'var(--teal-300)',
                borderRadius: '5px 5px 2px 2px',
                opacity: survol && survol.i === i ? 0.85 : 1,
              }}
            />
            <div style={{
              fontFamily: 'var(--font-mono)', fontSize: 'var(--t-eyebrow)', color: 'var(--ink-400)', textAlign: 'center',
            }}>{etiquette(i)}</div>
          </div>
        ))}
      </div>
      {survol && semaines[survol.i] ? (
        <ChartTip
          pointeur={{ x: survol.x, y: survol.y }}
          etiquette={t('sem. du {v}', { v: day(semaines[survol.i]!.start) })}
          valeur={t('{n} vente', { n: semaines[survol.i]!.qty })}
        />
      ) : null}
    </div>
  );
}

/**
 * Répartition en barres — relevé maquette l.4078-4092, rangée par rangée.
 *
 * ⚠️ LA GRILLE EST FIXE (libellé · barre · valeur), pas un `flex` : sur trois colonnes alignées, les
 * barres partent toutes du MÊME x et les valeurs finissent au même bord. En flex, un libellé plus long
 * décalait sa barre — et deux barres qui ne partent pas du même point ne se comparent plus à l’œil,
 * ce qui est pourtant la seule chose qu’on leur demande.
 * ⚠️ LES TAILLES EN MONO ET EN GRAS, les coloris en proportionnelle : une taille est un CODE (36, XL)
 * qu’on aligne, un coloris est un MOT qu’on lit — d’où aussi 46 px contre 86.
 */
function Repartition({ heading, parts, labelWidth, trait, mono }: {
  heading: string;
  parts: ArticleStats['bySize'];
  labelWidth: number;
  trait: string;
  mono?: boolean;
}) {
  const max = Math.max(1, ...parts.map((p) => p.qty));
  return (
    <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', padding: '15px 16px', minWidth: 0 }}>
      <div style={{ fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)', marginBottom: 11 }}>{heading}</div>
      {parts.length === 0
        ? <div style={{ fontSize: '12.5px', color: 'var(--ink-400)' }}>{t('Aucune vente à répartir.')}</div>
        : parts.map((p) => (
            <div key={p.label} style={{
              display: 'grid', gridTemplateColumns: `${labelWidth}px minmax(0, 1fr) 34px`,
              gap: 9, alignItems: 'center', padding: '3px 0',
            }}>
              <span style={{
                fontFamily: mono ? 'var(--font-mono)' : undefined,
                fontSize: '11.5px', fontWeight: mono ? 700 : 600, color: 'var(--ink-700)',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>{p.label}</span>
              <span style={{
                display: 'block', height: 10, borderRadius: 'var(--r-pill)',
                background: 'var(--ink-100)', overflow: 'hidden',
              }}>
                <span style={{
                  display: 'block', height: '100%', width: `${Math.round((p.qty / max) * 100)}%`,
                  background: trait, borderRadius: 'var(--r-pill)',
                }} />
              </span>
              <span style={{
                fontFamily: 'var(--font-mono)', fontSize: '11.5px', color: 'var(--ink-600)',
                textAlign: 'right', fontVariantNumeric: 'tabular-nums',
              }}>{p.qty}</span>
            </div>
          ))}
    </div>
  );
}
